import React, { useState } from 'react';
import { api } from '../../utils/api';

const COUNTRY_LABELS = { IT: '🇮🇹 Italia', ES: '🇪🇸 España' };

const NotificationsView = ({ 
  isDeviceRegistered,
  mandanti,
  myDevices,
  rebelnetCampaigns,
  trackingCategories = [],
  onRequestPermission,
  onRegisterDevice,
  onDeleteDevice,
  getUserId,
  loadMyDevices,
  country,
  countryPreferences,
  setCountryPreferences,
  supportedCountries
}) => {
  const [expandedSections, setExpandedSections] = useState({});

  const getSectionKey = (countryCode, section) => `${countryCode}-${section}`;

  const toggleSection = (countryCode, section) => {
    const key = getSectionKey(countryCode, section);
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isSectionExpanded = (countryCode, section) => {
    return !!expandedSections[getSectionKey(countryCode, section)];
  };

  const updatePreferencesOnServer = async (newCountryPreferences) => {
    const userId = getUserId();
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          const deviceId = btoa(subscription.endpoint).substring(0, 50);
          const currentCountryPrefs = newCountryPreferences[country] || [];
          await api.updatePreferences({
            userId,
            deviceId,
            enabledMandanti: currentCountryPrefs,
            countryPreferences: newCountryPreferences
          });
          loadMyDevices();
        }
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  const toggleCountryMandante = async (countryCode, mandante) => {
    const currentPrefs = countryPreferences[countryCode] || [];
    const newPrefs = currentPrefs.includes(mandante)
      ? currentPrefs.filter(m => m !== mandante)
      : [...currentPrefs, mandante];
    
    const newCountryPreferences = { ...countryPreferences, [countryCode]: newPrefs };
    setCountryPreferences(newCountryPreferences);
    await updatePreferencesOnServer(newCountryPreferences);
  };

  const toggleAllMandantiForCountry = async (countryCode) => {
    const currentPrefs = countryPreferences[countryCode] || [];
    const allSelected = mandanti.every(m => currentPrefs.includes(m));
    const withoutMandanti = currentPrefs.filter(p => !mandanti.includes(p));
    const newPrefs = allSelected
      ? withoutMandanti
      : [...new Set([...currentPrefs, ...mandanti])];
    
    const newCountryPreferences = { ...countryPreferences, [countryCode]: newPrefs };
    setCountryPreferences(newCountryPreferences);
    await updatePreferencesOnServer(newCountryPreferences);
  };

  const toggleAllRebelnetForCountry = async (countryCode) => {
    const currentPrefs = countryPreferences[countryCode] || [];
    const allSelected = rebelnetCampaigns.every(c => currentPrefs.includes(c));
    const withoutRebelnet = currentPrefs.filter(p => !rebelnetCampaigns.includes(p));
    const newPrefs = allSelected
      ? withoutRebelnet
      : [...new Set([...currentPrefs, ...rebelnetCampaigns])];
    
    const newCountryPreferences = { ...countryPreferences, [countryCode]: newPrefs };
    setCountryPreferences(newCountryPreferences);
    await updatePreferencesOnServer(newCountryPreferences);
  };

  const toggleOperationalForCountry = async (countryCode) => {
    const currentPrefs = countryPreferences[countryCode] || [];
    const newPrefs = currentPrefs.includes('OPERATIONAL')
      ? currentPrefs.filter(p => p !== 'OPERATIONAL')
      : [...currentPrefs, 'OPERATIONAL'];
    
    const newCountryPreferences = { ...countryPreferences, [countryCode]: newPrefs };
    setCountryPreferences(newCountryPreferences);
    await updatePreferencesOnServer(newCountryPreferences);
  };

  const countries = supportedCountries || ['IT', 'ES'];
  const showRebelnet = (countryCode) => countryCode === 'IT';

  return (
    <div className="settings">
      <h2>Notifiche Push</h2>
      
      {/* Notification status */}
      {Notification.permission === 'granted' && !isDeviceRegistered && (
        <div className="notification-setup">
          <p className="status-ok">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
            Notifiche abilitate nel browser
          </p>
          <p>Registra questo dispositivo per ricevere notifiche</p>
          <button className="btn-primary" onClick={onRegisterDevice}>Registra Dispositivo</button>
        </div>
      )}
      
      {Notification.permission === 'granted' && isDeviceRegistered && (
        <div className="notification-setup">
          <p className="status-ok">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
            Dispositivo registrato e notifiche attive
          </p>
        </div>
      )}
      
      {Notification.permission === 'default' && (
        <div className="notification-setup">
          <p>Abilita le notifiche per ricevere aggiornamenti in tempo reale</p>
          <button className="btn-primary" onClick={onRequestPermission}>Abilita Notifiche</button>
        </div>
      )}
      
      {Notification.permission === 'denied' && (
        <div className="notification-denied">
          Le notifiche sono state bloccate. Abilitale dalle impostazioni del browser.
        </div>
      )}

      {/* Preferences grouped by country */}
      {countries.map(countryCode => {
        const countryPrefs = countryPreferences[countryCode] || [];
        const isCurrentCountry = countryCode === country;

        return (
          <div key={countryCode} className="country-preferences-group" style={{ marginTop: '1.5rem' }}>
            <h3>{COUNTRY_LABELS[countryCode] || countryCode} {isCurrentCountry ? '(attivo)' : ''}</h3>

            {/* Mandanti Produzione */}
            <div className="section-group">
              <div className="section-header" onClick={() => toggleSection(countryCode, 'mandanti')}>
                <h4>Mandanti Produzione</h4>
                <div className="section-header-actions">
                  <button className="btn-primary btn-small" onClick={(e) => { e.stopPropagation(); toggleAllMandantiForCountry(countryCode); }}>
                    {mandanti.every(m => countryPrefs.includes(m)) ? 'Deseleziona' : 'Seleziona Tutti'}
                  </button>
                  <span className={`section-chevron ${isSectionExpanded(countryCode, 'mandanti') ? 'open' : ''}`}>▼</span>
                </div>
              </div>
              {isSectionExpanded(countryCode, 'mandanti') && (
                <div className="section-body">
                  <p className="section-body-hint">Seleziona le mandanti per cui ricevere notifiche:</p>
                  {mandanti.map(m => (
                    <label key={m} className="checkbox-label">
                      <input type="checkbox" checked={countryPrefs.includes(m)} onChange={() => toggleCountryMandante(countryCode, m)} />
                      {m}
                    </label>
                  ))}
                  {mandanti.length === 0 && <p className="section-body-hint">Nessuna mandante disponibile</p>}
                </div>
              )}
            </div>

            {/* Campagne Rebelnet - only for IT */}
            {showRebelnet(countryCode) && (
              <div className="section-group">
                <div className="section-header" onClick={() => toggleSection(countryCode, 'rebelnet')}>
                  <h4>Campagne Rebelnet</h4>
                  <div className="section-header-actions">
                    <button className="btn-primary btn-small" onClick={(e) => { e.stopPropagation(); toggleAllRebelnetForCountry(countryCode); }}>
                      {rebelnetCampaigns.every(c => countryPrefs.includes(c)) ? 'Deseleziona' : 'Seleziona Tutti'}
                    </button>
                    <span className={`section-chevron ${isSectionExpanded(countryCode, 'rebelnet') ? 'open' : ''}`}>▼</span>
                  </div>
                </div>
                {isSectionExpanded(countryCode, 'rebelnet') && (
                  <div className="section-body">
                    <p className="section-body-hint">Seleziona le campagne Rebelnet per cui ricevere notifiche:</p>
                    {rebelnetCampaigns.map(c => (
                      <label key={c} className="checkbox-label">
                        <input type="checkbox" checked={countryPrefs.includes(c)} onChange={() => toggleCountryMandante(countryCode, c)} />
                        {c}
                      </label>
                    ))}
                    {rebelnetCampaigns.length === 0 && <p className="section-body-hint">Nessuna campagna disponibile</p>}
                  </div>
                )}
              </div>
            )}

            {/* Notifiche Operative */}
            <div className="section-group">
              <div className="section-header" onClick={() => toggleSection(countryCode, 'operational')}>
                <h4>Notifiche Operative</h4>
                <span className={`section-chevron ${isSectionExpanded(countryCode, 'operational') ? 'open' : ''}`}>▼</span>
              </div>
              {isSectionExpanded(countryCode, 'operational') && (
                <div className="section-body">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={countryPrefs.includes('OPERATIONAL')} onChange={() => toggleOperationalForCountry(countryCode)} />
                    Ricevi notifiche operative (utenti registrati, errori sistema, ecc.)
                  </label>
                </div>
              )}
            </div>

            {/* Tracking / Altre Notifiche */}
            {trackingCategories.length > 0 && (
              <div className="section-group">
                <div className="section-header" onClick={() => toggleSection(countryCode, 'tracking')}>
                  <h4>Altre Notifiche</h4>
                  <span className={`section-chevron ${isSectionExpanded(countryCode, 'tracking') ? 'open' : ''}`}>▼</span>
                </div>
                {isSectionExpanded(countryCode, 'tracking') && (
                  <div className="section-body">
                    <p className="section-body-hint">Seleziona le categorie di tracking per cui ricevere notifiche:</p>
                    {trackingCategories.map(cat => (
                      <label key={cat} className="checkbox-label">
                        <input type="checkbox" checked={countryPrefs.includes(`TRACKING_${cat}`)} onChange={() => toggleCountryMandante(countryCode, `TRACKING_${cat}`)} />
                        {cat}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* My Devices */}
      <h3>I Miei Dispositivi</h3>
      <div className="subscriptions-list">
        {myDevices.map(device => (
          <div key={device.deviceId} className={`subscription-card ${device.isCurrentDevice ? 'current-device' : ''}`}>
            <div className="subscription-info">
              {device.isCurrentDevice && (
                <p><strong style={{ color: 'var(--color-success-dark)' }}>📱 Questo dispositivo</strong></p>
              )}
              {device.deviceInfo && <p><strong>Dispositivo:</strong> {device.deviceInfo}</p>}
              {device.createdAt && <p><strong>Registrato:</strong> {new Date(device.createdAt).toLocaleString('it-IT')}</p>}
              <p><strong>Notifiche attive:</strong> {device.enabledMandanti?.length > 0 ? device.enabledMandanti.join(', ') : 'Nessuna'}</p>
            </div>
            <button className="btn-danger" onClick={() => onDeleteDevice(device.userId, device.deviceId, device.isCurrentDevice)}>
              Elimina
            </button>
          </div>
        ))}
        {myDevices.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">📱</div>
            <div className="empty-state-text">Nessun dispositivo registrato</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsView;
